import { MGPOptional, MGPValidation } from '@everyboard/lib';
import { NumberConfig } from '../../config/NumberConfig';
import { RulesConfigDescription } from '../../config/RulesConfigDescription';
import { RulesConfigDescriptionLocalizable } from '../../config/RulesConfigDescriptionLocalizable';
import { GameNode } from '../../jscaip/AI/GameNode';
import { GameStatus } from '../../jscaip/GameStatus';
import { ConfigurableRules } from '../../jscaip/Rules';
import { MGPValidators } from '../../utils/MGPValidator';
import { ApagosFailure } from './ApagosFailure';
import { ApagosState } from './ApagosState';
export class ApagosNode extends GameNode {
}
export class ApagosRules extends ConfigurableRules {
    static singleton = MGPOptional.empty();
    static RULES_CONFIG_DESCRIPTION = new RulesConfigDescription({
        name: () => $localize `Apagos`,
        config: {
            width: new NumberConfig(4, RulesConfigDescriptionLocalizable.WIDTH, MGPValidators.range(2, 7)),
            increment: new NumberConfig(2, () => $localize `Increment`, MGPValidators.range(0, 3)),
        },
    });
    static get() {
        if (ApagosRules.singleton.isAbsent()) {
            ApagosRules.singleton = MGPOptional.of(new ApagosRules());
        }
        return ApagosRules.singleton.get();
    }
    getRulesConfigDescription() {
        return ApagosRules.RULES_CONFIG_DESCRIPTION;
    }
    getInitialState(config) {
        const width = config.width;
        const increment = config.increment;
        const zeroPieces = [];
        const onePieces = [];
        const sizes = [];
        let currentSize = 1; // the first square will always be 1
        let numberOfPieces = 0;
        for (let x = 0; x < width; x++) {
            zeroPieces.push(0);
            onePieces.push(0);
            sizes.push(currentSize);
            numberOfPieces += Math.ceil(currentSize / 2);
            currentSize += increment;
        }
        return ApagosState.fromRepresentation(0, [
            zeroPieces,
            onePieces,
            sizes.reverse(),
        ], numberOfPieces, numberOfPieces);
    }
    applyLegalMove(move, state, config, _info) {
        if (move.isDrop()) {
            return this.applyLegalDrop(move, state, config);
        }
        else {
            return this.applyLegalTransfer(move, state);
        }
    }
    applyLegalDrop(move, state, config) {
        const remaining = state.getRemainingCopy();
        remaining.add(move.piece.get(), -1);
        const nextTurnState = new ApagosState(state.turn + 1, state.board, remaining);
        const piece = move.piece.get();
        const newSquare = nextTurnState.getPieceAt(move.landing).addPiece(piece);
        if (move.landing === config.width - 1) {
            return nextTurnState.updateAt(move.landing, newSquare);
        }
        else {
            const descendingX = move.landing + 1;
            const descendingSquare = nextTurnState.getPieceAt(descendingX);
            const intermediaryState = nextTurnState.updateAt(move.landing, descendingSquare);
            return intermediaryState.updateAt(descendingX, newSquare);
        }
    }
    applyLegalTransfer(move, state) {
        const currentPlayer = state.getCurrentPlayer();
        const starting = move.starting.get();
        const newStartingSquare = state.getPieceAt(starting).subtractPiece(currentPlayer);
        const newLandingSquare = state.getPieceAt(move.landing).addPiece(currentPlayer);
        let resultingState = state.updateAt(starting, newStartingSquare);
        resultingState = resultingState.updateAt(move.landing, newLandingSquare);
        return new ApagosState(resultingState.turn + 1, resultingState.board, resultingState.remaining);
    }
    isLegal(move, state) {
        if (state.getPieceAt(move.landing).isFull()) {
            return MGPValidation.failure(ApagosFailure.CANNOT_LAND_ON_A_FULL_SQUARE());
        }
        if (move.isDrop()) {
            return this.isLegalDrop(move, state);
        }
        else {
            return this.isLegalSlideDown(move, state);
        }
    }
    isLegalDrop(move, state) {
        if (state.getRemaining(move.piece.get()) <= 0) {
            return MGPValidation.failure(ApagosFailure.NO_PIECE_REMAINING_TO_DROP());
        }
        return MGPValidation.SUCCESS;
    }
    isLegalSlideDown(move, state) {
        const currentPlayer = state.getCurrentPlayer();
        const startingSquare = state.getPieceAt(move.starting.get());
        if (startingSquare.count(currentPlayer) === 0) {
            return MGPValidation.failure(ApagosFailure.NO_PIECE_OF_YOU_IN_CHOSEN_SQUARE());
        }
        return MGPValidation.SUCCESS;
    }
    getGameStatus(node, config) {
        const width = config.width;
        const state = node.gameState;
        for (let x = 0; x < width; x++) {
            if (state.getPieceAt(x).isFull() === false) {
                return GameStatus.ONGOING;
            }
        }
        for (let x = width - 1; x >= 0; x--) {
            const dominating = state.getPieceAt(x).getDominatingPlayer();
            if (dominating.isPlayer()) {
                return GameStatus.getVictory(dominating);
            }
        }
        return GameStatus.DRAW;
    }
}
